import { useEffect, useState } from 'react';
import { HiOutlineCloudUpload } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';

import ShopifyLogo from '@/assets/ShopifyLogo';
import Button from '@/components/ui/button';
import Loader from '@/components/ui/loader/Loader';
import { installationInstructions } from '@/components/utils/constants';
import { useUser } from '@/hooks/useUser';
import { useUserStore } from '@/hooks/useUserStore';
import { InventoryType } from '@/interfaces/api/InventoryType';
import { AppRoutes } from '@/interfaces/routes/AppRoutes';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const Store = () => {
	const navigate = useNavigate();
	const shopifyInstallUrl = import.meta.env.VITE_SHOPIFY_INSTALL_URL;
	const [storeName, setStoreName] = useState('');
	const [businessName, setBusinessName] = useState('');
	const [storeNameError, setStoreNameError] = useState('');
	const [isCreatingStore, setIsCreatingStore] = useState(false);
	const [selectedOption, setSelectedOption] = useState<InventoryType | null>(
		null,
	);

	const { store, isLoading: userStoreIsLoading } = useUserStore({
		retry: false,
	});
	const { setUserInfo, user } = useUser();

	useEffect(() => {
		if (!userStoreIsLoading && store && store.id) {
			navigate(AppRoutes.UserDashboard);
		}
	}, [store, userStoreIsLoading, navigate]);

	const createCsvStore = async (type: InventoryType) => {
		try {
			if (!storeName.trim()) {
				notificationService.error('Please enter a store name');
				return;
			}
			if (!businessName.trim()) {
				notificationService.error('Please enter a business name');
				return;
			}

			setIsCreatingStore(true);
			await haggleApiService.createStoreWithUserDetails({
				name: storeName,
				tradeName: storeName,
				accessToken: storeName,
				inventoryType: type,
				businessName: businessName,
			});
			if (user) {
				setUserInfo({
					...user,
					inventoryType: type,
					businessName: businessName,
				});
			}
			notificationService.success(
				`Store created successfully  with inventory type updated to ${type}`,
			);
			navigate(AppRoutes.UserDashboard);
		} catch (error) {
			console.error(error);
			notificationService.error(`${error}`);
		} finally {
			setIsCreatingStore(false);
		}
	};

	const handleCardKeyDown =
		(option: InventoryType) => (e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.key === 'Enter' || e.key === ' ') {
				setSelectedOption(option);
			}
		};

	const handleBack = () => setSelectedOption(null);

	return (
		<div
			className="max-w-full flex flex-col items-center h-full my-4"
			data-test="registration-page"
		>
			{userStoreIsLoading ? (
				<Loader className="flex items-center justify-center h-full w-full" />
			) : (
				<>
					<h2
						className="text-2xl md:text-3xl mb-2 md:mb-6 mt-4"
						data-testid="registration-title"
					>
						Become a Haggle Member
					</h2>
					<p
						className="text-center font-normal text-sm px-8 mb-2 md:mb-6 text-gray-700"
						data-testid="registration-description"
					>
						Create your Haggle Member profile, and get access to an enhanced
						shopping experience.
					</p>

					{/* Initial two-card layout */}
					{!selectedOption && (
						<div className="w-full max-w-5xl grid grid-cols-1 sm:grid-cols-2 gap-10 px-6 mt-12">
							{/* Shopify Card */}
							<div
								className="flex flex-col items-center justify-center bg-white/80 rounded-2xl shadow-2xl p-12 min-h-[320px] min-w-[280px] cursor-pointer border-4 border-transparent hover:border-purpleBrand-500 focus-within:border-purpleBrand-500 transition outline-none ring-1 ring-gray-200 hover:ring-purpleBrand-300 focus:ring-purpleBrand-400"
								tabIndex={0}
								aria-label="Connect using Shopify store"
								onClick={() => setSelectedOption(InventoryType.SHOPIFY)}
								onKeyDown={handleCardKeyDown(InventoryType.SHOPIFY)}
								role="button"
							>
								<ShopifyLogo width={100} height={100} />
								<span className="mt-2 text-xl font-bold text-gray-800">
									Connect using Shopify store
								</span>
							</div>
							{/* Upload Card */}
							<div
								className="flex flex-col items-center justify-center bg-white/80 rounded-2xl shadow-2xl p-12 min-h-[320px] min-w-[280px] cursor-pointer border-4 border-transparent hover:border-purpleBrand-500 focus-within:border-purpleBrand-500 transition outline-none ring-1 ring-gray-200 hover:ring-purpleBrand-300 focus:ring-purpleBrand-400"
								tabIndex={0}
								aria-label="Upload Own Inventory"
								onClick={() => setSelectedOption(InventoryType.CSV)}
								onKeyDown={handleCardKeyDown(InventoryType.CSV)}
								role="button"
							>
								<HiOutlineCloudUpload
									className="w-20 h-20 text-purpleBrand-500 mb-4"
									aria-label="Upload logo"
								/>
								<span className="mt-2 text-xl font-bold text-gray-800">
									Not on Shopify?
								</span>
								<span className="mt-2 text-xl font-bold text-gray-800">
									Create your account here
								</span>
							</div>
						</div>
					)}

					{/* Expanded Shopify View */}
					{selectedOption === InventoryType.SHOPIFY && (
						<div className="w-full grid grid-cols-1 md:grid-cols-2 gap-8 px-10 mt-8 items-start">
							<Button
								variant="solid"
								shape="rounded"
								size="small"
								className="ml-8 w-fit bg-purpleBrand-500 hover:bg-purpleBrand-600 rounded-md active:bg-purpleBrand-900"
								onClick={handleBack}
								aria-label="Back to options"
							>
								Back
							</Button>
							<div></div>
							<div className="bg-gradient-to-br from-purpleBrand-50 to-white rounded-xl p-8 ml-8 shadow-md">
								<h3 className="font-bold text-2xl mb-6 uppercase flex items-center gap-3">
									<ShopifyLogo width={60} height={60} />
									Connect using Shopify store
								</h3>
								<ol className="mb-8 space-y-3 list-decimal pl-5">
									{installationInstructions.map((instruction, index) => (
										<li key={index} className="pl-2">
											<span className="text-base mb-2 text-gray-700">
												<strong className="font-semibold">
													{instruction.title}
												</strong>{' '}
												- {instruction.description}
											</span>
										</li>
									))}
								</ol>
								<div className="flex mb-4 justify-center">
									<Button
										variant="solid"
										shape="rounded"
										className="bg-purpleBrand-500 hover:bg-purpleBrand-600 rounded-md active:bg-purpleBrand-900"
										size="large"
										onClick={(e) => {
											e.stopPropagation();
											window.open(shopifyInstallUrl, '_blank');
										}}
										data-test="shopify-guide-button"
									>
										Install Haggle App
									</Button>
								</div>
							</div>
							<div className="flex items-start h-full justify-center">
								<div className="w-full h-full rounded-xl overflow-hidden shadow-md bg-gradient-to-br from-purpleBrand-50 to-white flex items-center justify-center">
									<iframe
										className="w-full h-full rounded-md"
										src={import.meta.env.VITE_VIDEO_URL}
										allow="autoplay"
										allowFullScreen
										data-test="guide-iframe"
										title="Shopify Guide"
									></iframe>
								</div>
							</div>
						</div>
					)}

					{/* Expanded Upload View */}
					{selectedOption === InventoryType.CSV && (
						<div className="w-full grid grid-cols-1 md:grid-cols-2 gap-8 px-10 mt-8 items-start">
							<Button
								variant="solid"
								shape="rounded"
								size="small"
								className="w-fit bg-purpleBrand-500 hover:bg-purpleBrand-600 rounded-md active:bg-purpleBrand-900"
								onClick={handleBack}
								aria-label="Back to options"
							>
								Back
							</Button>
							<div></div>
							<div className="bg-gradient-to-br from-purpleBrand-50 to-white rounded-xl p-8 shadow-md h-full">
								<h3 className="font-bold text-2xl mb-6 uppercase flex items-center gap-3">
									<HiOutlineCloudUpload
										className="w-10 h-10 text-purpleBrand-500"
										aria-label="Upload logo"
									/>
									Create your account
								</h3>
								<ol className="mb-8 space-y-4 list-decimal pl-5">
									<li className="pl-2">
										<span className="text-base mb-4 text-gray-700">
											<strong className="font-semibold">
												Download Template
											</strong>{' '}
											- Use our CSV template for proper formatting
										</span>
									</li>
									<li className="pl-2">
										<span className="text-base mb-4 text-gray-700">
											<strong className="font-semibold">Fill Data</strong> - Add
											your product information to the template
										</span>
									</li>
									<li className="pl-2">
										<span className="text-base mb-4 text-gray-700">
											<strong className="font-semibold">Upload</strong> - Submit
											your completed CSV file
										</span>
									</li>
								</ol>
							</div>

							<div className="flex flex-col items-start justify-start h-full">
								<div className="bg-gradient-to-br from-purpleBrand-50 to-white rounded-xl p-8 shadow-md flex flex-col h-full w-full">
									<label
										htmlFor="storeName"
										className="block text-base font-semibold text-gray-700 mb-2"
									>
										Trade Name
									</label>
									<input
										type="text"
										id="storeName"
										value={storeName}
										onChange={(e) => {
											const inputValue = e.target.value;
											// Check if the input contains invalid characters
											const hasInvalidChars = /[^a-zA-Z0-9-]/.test(inputValue);

											if (hasInvalidChars) {
												setStoreNameError(
													'Only letters, numbers, and hyphens are allowed. No spaces or special characters.',
												);
												// Clear error after 3 seconds
												setTimeout(() => setStoreNameError(''), 3000);
											} else {
												setStoreNameError('');
											}

											// Only allow letters, numbers, and hyphens (no spaces)
											const validValue = inputValue.replace(
												/[^a-zA-Z0-9-]/g,
												'',
											);
											setStoreName(validValue);
										}}
										className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purpleBrand-500 focus:border-purpleBrand-500 ${
											storeNameError
												? 'border-red-500 mb-1'
												: 'border-gray-300 mb-6'
										}`}
										placeholder="Enter your trade name (letters, numbers, and hyphens only) e.g. 'Haggle-Store'"
										required
									/>
									{storeNameError && (
										<p className="text-red-500 text-sm mb-4">
											{storeNameError}
										</p>
									)}

									<label
										htmlFor="businessName"
										className="block text-base font-semibold text-gray-700 mb-2"
									>
										Business Name
									</label>
									<input
										type="text"
										id="businessName"
										value={businessName}
										onChange={(e) => setBusinessName(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purpleBrand-500 focus:border-purpleBrand-500 mb-6"
										placeholder="Enter your business name e.g. 'Haggle Store'"
										required
									/>
									<div className="mt-auto w-full">
										<Button
											variant="solid"
											shape="rounded"
											className="bg-purpleBrand-500 hover:bg-purpleBrand-600 rounded-md active:bg-purpleBrand-900"
											size="large"
											isLoading={isCreatingStore}
											onClick={() => {
												createCsvStore(InventoryType.CSV);
											}}
											data-test="csv-upload-button"
										>
											Create Store
										</Button>
									</div>
								</div>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default Store;
